import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Sparkles,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Search,
  GripVertical,
  ShoppingBag,
  TrendingUp,
  Eye,
  MousePointerClick,
  Loader,
  AlertCircle,
  CheckCircle,
  X,
  BarChart2,
  ArrowUp,
  ArrowDown,
  Package,
  ChevronLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

// ─── Toast notification ───────────────────────────────────────────────────────
function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white font-montserrat text-sm font-semibold animate-slide-up ${
      type === 'success' ? 'bg-brand-green' : 'bg-red-500'
    }`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {message}
      <button onClick={onClose} className="ml-1 opacity-70 hover:opacity-100">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Stats Card ───────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-xs text-slate-500 font-montserrat">{label}</p>
          <p className="text-xl font-bold text-slate-900 font-montserrat">{value}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Product Search Modal ─────────────────────────────────────────────────────
function ProductSearchModal({ onSelect, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      searchProducts(query.trim().length >= 1 ? query : '');
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [query]);

  useEffect(() => { searchProducts(''); }, []);

  const searchProducts = async (q = query) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/products/search`, { params: { q, limit: 20 } });
      setResults(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-brand-gold" />
            <h3 className="font-bold text-slate-900 font-montserrat">Seleccionar Producto</h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Buscar por nombre o SKU..."
              className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm font-montserrat focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-6 h-6 text-brand-primary animate-spin" />
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Package className="w-10 h-10 mb-2" />
              <p className="text-sm font-montserrat">No se encontraron productos</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {results.map(product => (
                <button
                  key={product.id}
                  onClick={() => !product.already_featured && onSelect(product)}
                  disabled={product.already_featured}
                  className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${
                    product.already_featured
                      ? 'opacity-50 cursor-not-allowed bg-slate-50'
                      : 'hover:bg-brand-primary/5 active:bg-brand-primary/10'
                  }`}
                >
                  <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-5 h-5 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 font-montserrat truncate">{product.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-slate-500 font-montserrat">{product.seller_name}</span>
                      <span className="text-xs text-slate-300">·</span>
                      <span className="text-xs text-slate-500 font-montserrat">SKU: {product.sku}</span>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-brand-primary font-montserrat">
                      S/ {parseFloat(product.price).toFixed(2)}
                    </p>
                    <div className="flex items-center gap-1 justify-end mt-0.5">
                      {product.already_featured && (
                        <span className="text-[10px] bg-brand-gold/20 text-brand-gold font-semibold px-1.5 py-0.5 rounded-full font-montserrat">
                          Ya destacado
                        </span>
                      )}
                      {!product.in_stock && !product.already_featured && (
                        <span className="text-[10px] bg-red-100 text-red-600 font-semibold px-1.5 py-0.5 rounded-full font-montserrat">
                          Sin stock
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Featured Ad Row ──────────────────────────────────────────────────────────
function FeaturedAdRow({ ad, onToggle, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleToggle = async () => { setToggling(true); await onToggle(ad); setToggling(false); };
  const handleDelete = async () => {
    if (!window.confirm(`¿Eliminar "${ad.title}" de los anuncios destacados?`)) return;
    setDeleting(true);
    await onDelete(ad.id);
    setDeleting(false);
  };

  return (
    <div className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
      ad.is_active ? 'bg-white border-slate-200 shadow-sm' : 'bg-slate-50 border-slate-200 opacity-60'
    }`}>
      <div className="flex flex-col gap-0.5 cursor-grab text-slate-300 flex-shrink-0">
        <GripVertical className="w-4 h-4" />
      </div>
      <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-bold text-slate-500 font-montserrat">{ad.display_order}</span>
      </div>
      <div className="w-12 h-12 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
        {ad.image_url ? (
          <img src={ad.image_url} alt={ad.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-slate-300" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-900 font-montserrat truncate">{ad.title}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <span className="text-xs text-slate-500 font-montserrat">{ad.seller_name}</span>
          <span className="text-xs text-slate-300">·</span>
          <span className="text-sm font-bold text-brand-primary font-montserrat">
            S/ {parseFloat(ad.price).toFixed(2)}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-0.5 flex-shrink-0">
        <button onClick={() => onMoveUp(ad)} disabled={isFirst} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30 transition-colors" title="Subir">
          <ArrowUp className="w-3.5 h-3.5 text-slate-500" />
        </button>
        <button onClick={() => onMoveDown(ad)} disabled={isLast} className="p-1 hover:bg-slate-100 rounded disabled:opacity-30 transition-colors" title="Bajar">
          <ArrowDown className="w-3.5 h-3.5 text-slate-500" />
        </button>
      </div>
      <button onClick={handleToggle} disabled={toggling} className="flex-shrink-0 transition-colors" title={ad.is_active ? 'Desactivar' : 'Activar'}>
        {toggling ? <Loader className="w-6 h-6 text-slate-400 animate-spin" /> : ad.is_active
          ? <ToggleRight className="w-7 h-7 text-brand-green" />
          : <ToggleLeft className="w-7 h-7 text-slate-400" />}
      </button>
      <button onClick={handleDelete} disabled={deleting} className="flex-shrink-0 p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Eliminar">
        {deleting ? <Loader className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
      </button>
    </div>
  );
}

// ─── Analytics Tab ────────────────────────────────────────────────────────────
function AnalyticsTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/ads/analytics`)
      .then(res => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-16">
      <Loader className="w-8 h-8 text-brand-primary animate-spin" />
    </div>
  );

  if (data.length === 0) return (
    <div className="flex flex-col items-center justify-center py-16 text-slate-400">
      <BarChart2 className="w-12 h-12 mb-3" />
      <p className="font-montserrat">No hay datos de analytics aún</p>
    </div>
  );

  const maxImpressions = Math.max(...data.map(d => d.impressions), 1);

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500 font-montserrat mb-4">Últimos 30 días · ordenado por clics</p>
      {data.map(row => (
        <div key={row.product_id} className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-start justify-between mb-2">
            <p className="text-sm font-semibold text-slate-900 font-montserrat line-clamp-1 flex-1 mr-4">{row.title}</p>
            <span className="text-sm font-bold text-brand-primary font-montserrat flex-shrink-0">{row.ctr}% CTR</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-1.5 mb-2">
            <div className="bg-brand-primary h-1.5 rounded-full transition-all" style={{ width: `${(row.impressions / maxImpressions) * 100}%` }} />
          </div>
          <div className="flex items-center gap-4 text-xs text-slate-500 font-montserrat">
            <div className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /><span>{row.impressions.toLocaleString()} impresiones</span></div>
            <div className="flex items-center gap-1"><MousePointerClick className="w-3.5 h-3.5" /><span>{row.clicks.toLocaleString()} clics</span></div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main Admin Page ──────────────────────────────────────────────────────────
export function AdminFeaturedAds({ setActiveComponent }) {
  const [featuredAds, setFeaturedAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('featured');
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => setToast({ message, type });

  const fetchFeaturedAds = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/featured-ads`);
      setFeaturedAds(res.data);
    } catch (err) {
      console.error(err);
      showToast('Error al cargar anuncios destacados', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFeaturedAds(); }, []);

  const handleAddProduct = async (product) => {
    setShowModal(false);
    try {
      await axios.post(`${API_URL}/api/admin/featured-ads`, { product_id: product.id });
      await fetchFeaturedAds();
      showToast(`"${product.title}" agregado a destacados`);
    } catch (err) {
      showToast(err.response?.data?.error || 'Error al agregar producto', 'error');
    }
  };

  const handleToggle = async (ad) => {
    try {
      await axios.patch(`${API_URL}/api/admin/featured-ads/${ad.id}`, { is_active: !ad.is_active });
      await fetchFeaturedAds();
      showToast(`Anuncio ${!ad.is_active ? 'activado' : 'desactivado'}`);
    } catch { showToast('Error al actualizar', 'error'); }
  };

  const handleDelete = async (adId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/featured-ads/${adId}`);
      await fetchFeaturedAds();
      showToast('Anuncio eliminado');
    } catch { showToast('Error al eliminar', 'error'); }
  };

  const handleMove = async (ad, direction) => {
    const idx = featuredAds.findIndex(a => a.id === ad.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= featuredAds.length) return;

    const updated = [...featuredAds];
    const tempOrder = updated[idx].display_order;
    updated[idx] = { ...updated[idx], display_order: updated[swapIdx].display_order };
    updated[swapIdx] = { ...updated[swapIdx], display_order: tempOrder };
    setFeaturedAds(updated.sort((a, b) => a.display_order - b.display_order));

    try {
      await axios.post(`${API_URL}/api/admin/featured-ads/reorder`, [
        { id: updated[idx].id,     display_order: updated[idx].display_order     },
        { id: updated[swapIdx].id, display_order: updated[swapIdx].display_order },
      ]);
    } catch {
      showToast('Error al reordenar', 'error');
      fetchFeaturedAds();
    }
  };

  const activeCount   = featuredAds.filter(a => a.is_active).length;
  const inactiveCount = featuredAds.filter(a => !a.is_active).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">

      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary to-pink-600 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">

            <div className="flex items-center gap-3">
              {/* ── Back button ── */}
              <button
                onClick={() => setActiveComponent('OptionsAdmin')}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors"
                title="Volver a opciones"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>

              <div className="bg-white p-2.5 rounded-xl shadow-md">
                <Sparkles className="w-6 h-6 text-brand-gold" />
              </div>
              <div>
                <h1 className="text-xl font-extrabold text-white font-montserrat tracking-tight">
                  Anuncios Destacados
                </h1>
                <p className="text-xs text-white/80 font-montserrat">
                  Gestiona los productos que aparecen en las páginas de seguimiento
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white text-brand-primary font-bold font-montserrat text-sm rounded-xl hover:shadow-md active:scale-95 transition-all"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Agregar</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <StatCard icon={Sparkles}   label="Total"     value={featuredAds.length} color="bg-brand-primary" />
          <StatCard icon={TrendingUp} label="Activos"   value={activeCount}        color="bg-brand-green"   />
          <StatCard icon={ToggleLeft} label="Inactivos" value={inactiveCount}      color="bg-slate-400"     />
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-200 p-1 rounded-xl mb-6">
          {[
            { id: 'featured',  label: 'Destacados', icon: Sparkles  },
            { id: 'analytics', label: 'Analytics',  icon: BarChart2 },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold font-montserrat text-sm transition-all ${
                activeTab === tab.id
                  ? 'bg-white text-brand-primary shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'featured' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader className="w-8 h-8 text-brand-primary animate-spin" />
              </div>
            ) : featuredAds.length === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center">
                <Sparkles className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 font-semibold font-montserrat mb-1">No hay anuncios destacados</p>
                <p className="text-sm text-slate-400 font-montserrat mb-4">
                  Agrega productos para que aparezcan primero en las páginas de seguimiento
                </p>
                <button
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-primary text-white font-bold font-montserrat text-sm rounded-xl hover:bg-brand-primary/90 active:scale-95 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Agregar primer producto
                </button>
              </div>
            ) : (
              <>
                <div className="bg-brand-gold/10 border border-brand-gold/30 rounded-xl p-3 mb-4 flex gap-2">
                  <Sparkles className="w-4 h-4 text-brand-gold flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-700 font-montserrat">
                    Los productos activos aparecen primero en todas las páginas de seguimiento. Máximo 4 destacados se muestran a la vez; el resto son automáticos.
                  </p>
                </div>
                <div className="space-y-2">
                  {featuredAds.map((ad, i) => (
                    <FeaturedAdRow
                      key={ad.id}
                      ad={ad}
                      onToggle={handleToggle}
                      onDelete={handleDelete}
                      onMoveUp={(a)  => handleMove(a, 'up')}
                      onMoveDown={(a) => handleMove(a, 'down')}
                      isFirst={i === 0}
                      isLast={i === featuredAds.length - 1}
                    />
                  ))}
                </div>
                <button
                  onClick={() => setShowModal(true)}
                  className="mt-4 w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-300 rounded-xl text-slate-500 hover:border-brand-primary hover:text-brand-primary font-montserrat font-semibold text-sm transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Agregar otro producto
                </button>
              </>
            )}
          </>
        )}

        {activeTab === 'analytics' && <AnalyticsTab />}
      </div>

      {showModal && (
        <ProductSearchModal onSelect={handleAddProduct} onClose={() => setShowModal(false)} />
      )}

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .animate-slide-up { animation: slide-up 0.25s ease forwards; }
      `}</style>
    </div>
  );
}

export default AdminFeaturedAds;